import { computed, onBeforeUnmount, ref } from 'vue'
import { headerState } from '../stores/header'
import api from '../../api/v2/api'
import { RequestMode } from '../../api/v2/api/api_constant'
import Exam from '../models/exam'
import Question, { ExamTestQuestion, GroupQuestion } from '../../api/v2/models/question'
import AsForm from '../../api/v2/forms/as'
import ExamStatusForm from '../../api/v2/forms/answer_status'

// Instance-local state: opening a classroom must not overwrite the standalone exam store.
export function useExamArtifact(onAnswered: () => void = () => {}) {
  const exam = ref<Exam | null>(null)
  const attemptId = ref(0)
  const items = ref<ExamTestQuestion[]>([])
  const questions = ref<Question[]>([])
  const index = ref(0)
  const phase = ref<'ready' | 'test' | 'feedback' | 'result' | 'review'>('ready')
  const busy = ref(false)
  const error = ref('')
  const now = ref(Date.now())
  let disposed = false
  let revision = 0
  const timer = setInterval(() => { now.value = Date.now() }, 1000)
  onBeforeUnmount(() => { disposed = true; revision++; clearInterval(timer) })

  const attempt = computed(() => exam.value?.userExams.find(a => a.id === attemptId.value))
  const groups = computed(() => Array.from(new Set(items.value.map(q => q.group))).map(group => items.value.filter(q => q.group === group)))
  const answered = computed(() => items.value.filter(q => !q.isDescriptionQuestion && q.isAnswered).length)
  const count = computed(() => items.value.filter(q => !q.isDescriptionQuestion).length)
  const canReview = computed(() => !!attempt.value?.finishedAt && !exam.value?.hideResult && !attempt.value?.isWaitingScoreCheck)
  const remaining = computed(() => {
    if (!exam.value || !attempt.value?.startedAt) return 0
    return Math.max(0, Math.ceil((attempt.value.startedAt.getTime() + exam.value.timeLimit * 60000 - now.value) / 1000))
  })
  const group = computed(() => new GroupQuestion(questions.value[0]?.group ?? 0, questions.value))

  async function run(action: () => Promise<void>) {
    if (busy.value || disposed) return
    busy.value = true
    error.value = ''
    try { await action() } catch (e: any) {
      if (!disposed) error.value = typeof e?.messages === 'string' ? e.messages : e?.message || '処理できませんでした。もう一度お試しください。'
    } finally { if (!disposed) busy.value = false }
  }

  function setExam(value: Exam, id?: number) {
    exam.value = value
    attemptId.value = id || value.userExam?.id || 0
    // The existing input components use exam.userExam for draft keys.
    value.userExams.sort((a, b) => Number(b.id === attemptId.value) - Number(a.id === attemptId.value))
  }

  async function refreshItems() { items.value = await api.examQuestionApi.get(attemptId.value) }

  async function fetchGroup(review = false) {
    const first = groups.value[index.value]?.[0]
    // 前の問題は消さない（消すと、次が届くまで欄がつぶれて、また広がる）。次が届いてから入れ替える
    if (!first) { questions.value = []; return }
    const value = review
      ? await api.examQuestionApi.showResult(attemptId.value, first.id)
      : await api.examQuestionApi.show(attemptId.value, first.id)
    if (disposed) return
    questions.value = value
    // The compact result endpoint omits question bodies. The existing review
    // renderer resolves saved answers through exam.questions.
    if (review && exam.value) value.forEach(q => {
      const position = exam.value!.questions.findIndex(item => item.id === q.id)
      if (position >= 0) exam.value!.questions.splice(position, 1, q)
      else exam.value!.questions.push(q)
    })
    phase.value = review ? 'review' : 'test'
    if (!review && exam.value?.isResultAfterQuestion && !exam.value.hideResult && groups.value[index.value].every(q => q.isDescriptionQuestion || q.isAnswered)) {
      phase.value = 'feedback'
    }
  }

  // loading：テストを開いて、どの画面（準備・テスト中・結果）を出すかが決まるまで
  const loading = ref(false)
  async function load(id: number, userExamId?: number) {
    const request = ++revision
    loading.value = true
    try { await loadExam(id, request, userExamId) } finally { if (request === revision && !disposed) loading.value = false }
  }
  async function loadExam(id: number, request: number, userExamId?: number) {
    await run(async () => {
      const value = await api.exam.showForTest(id, {})
      if (disposed || request !== revision) return
      setExam(value, userExamId)
      index.value = 0
      questions.value = []
      items.value = []
      if (attempt.value?.finishedAt) {
        setExam(await api.exam.showForResult(id, {}), attemptId.value)
        phase.value = 'result'
        if (canReview.value) await refreshItems()
      } else if (attempt.value?.startedAt) {
        await refreshItems()
        const next = groups.value.findIndex(g => g.some(q => !q.isDescriptionQuestion && !q.isAnswered))
        index.value = Math.max(0, next)
        await fetchGroup()
      } else { phase.value = 'ready' }
    })
  }

  async function start() {
    await run(async () => {
      if (!exam.value) return
      const form = new ExamStatusForm()
      form.examId = exam.value.id
      form.isStartExam = true
      if (attempt.value) form.userExamId = attempt.value.id
      setExam(await api.examStatus.patch(exam.value.id, form))
      await refreshItems()
      index.value = 0
      await fetchGroup()
    })
  }

  async function select(next: number, review = false) {
    if (next < 0 || next >= groups.value.length || (review && !canReview.value)) return
    await run(async () => { index.value = next; await fetchGroup(review) })
  }

  async function submit(values: Record<number, string[]>) {
    await run(async () => {
      if (!exam.value || !attempt.value || phase.value !== 'test') return
      if (remaining.value <= 0) throw new Error('制限時間を過ぎました。「提出して結果を見る」を押してください。')
      const answerable = questions.value.filter(q => !q.isDescriptionQuestion)
      if (answerable.some(q => !values[q.id]?.some(v => v?.trim()))) throw new Error('回答を入力してください。')
      const mode = exam.value.isResultAfterQuestion && !exam.value.hideResult ? RequestMode.DETAIL : RequestMode.TEST
      for (const q of answerable) {
        const form = new AsForm()
        Object.assign(form, { examId: exam.value.id, userExamId: attemptId.value, qId: String(q.id), answers: values[q.id] })
        await api.as.create(form, mode)
      }
      await refreshItems()
      if (answerable.some(q => !items.value.find(item => item.id === q.id)?.isAnswered)) throw new Error('回答の保存を確認できませんでした。もう一度お試しください。')
      onAnswered()
      await fetchGroup()
      // Retain a question-by-question result in the artifact when the exam permits it.
      if (mode === RequestMode.DETAIL && answerable.length) phase.value = 'feedback'
      else if (index.value + 1 < groups.value.length) { index.value++; await fetchGroup() }
    })
  }

  async function finish() {
    await run(async () => {
      if (!exam.value || !attempt.value) return
      const form = new ExamStatusForm()
      Object.assign(form, { examId: exam.value.id, userExamId: attemptId.value, isFinishExam: true })
      await api.examStatus.patch(exam.value.id, form)
      // Existing grading path; do not show final marks before it has completed.
      if (headerState.schoolSetting?.useAiGrading) await api.as.aiGrade([], exam.value.id, '', attemptId.value)
      setExam(await api.exam.showForResult(exam.value.id, {}), attemptId.value)
      phase.value = 'result'
      questions.value = []
      if (canReview.value) await refreshItems()
    })
  }

  // type：再テストの種類（'' は同じ問題を最初から）。examId：対象のテスト（省略時はいま開いているテスト）
  async function retest(type: string, examId?: number): Promise<number | undefined> {
    let id: number | undefined
    await run(async () => {
      if (!exam.value?.canRetest || !canReview.value) return
      const result = await api.exam.rejoin(examId ?? exam.value.id, type)
      id = result.examId
      setExam(await api.exam.showForTest(result.examId, {}), result.userExamId)
      phase.value = 'ready'
      items.value = []; questions.value = []; index.value = 0
    })
    return id
  }

  return { exam, attempt, items, questions, index, phase, busy, error, groups, answered, count, canReview, remaining, group, loading, load, start, select, submit, finish, retest, run }
}
